import React, { useState, useEffect } from 'react';
import { Modal, Input, Checkbox, Table, notification, Button } from 'antd';

const { TextArea } = Input;

const AddInstructionsDialog = ({ 
    visible, 
    onCancel, 
    onOk, 
    selectedLetters, 
    stackType,
    stackId,
    isGrouped,
    summaryOrder
}) => {
    const [tableData, setTableData] = useState([]);
    const [bulkInstructions, setBulkInstructions] = useState('');
    const [bulkSkipAnswer, setBulkSkipAnswer] = useState(true);
    const [letterData, setLetterData] = useState({});

    // Initialize data when dialog opens
    useEffect(() => {
        if (visible && selectedLetters.length > 0) {
            // Sort letters based on summaryOrder if available
            let sortedLetters = [...selectedLetters];
            if (summaryOrder && Object.keys(summaryOrder).length > 0) {
                sortedLetters.sort((a, b) => {
                    const orderA = summaryOrder[a.id] || Number.MAX_SAFE_INTEGER;
                    const orderB = summaryOrder[b.id] || Number.MAX_SAFE_INTEGER;
                    return orderA - orderB;
                });
            }
            
            const data = sortedLetters.map((letter, index) => ({
                key: letter.id,
                letter: letter,
                senderName: `${letter.originalLetter?.sender?.firstName || ''} ${letter.originalLetter?.sender?.lastName || ''}`.trim(),
                letterId: letter.originalLetter?.letterId || 'N/A',
                index: index
            }));
            setTableData(data);
            
            // Initialize letter data with existing instruction comments or default values
            const initialLetterData = {};
            let hasExistingInstructions = false;
            
            selectedLetters.forEach(letter => {
                // Check if letter has existing instruction comments
                const existingInstructions = letter.comments?.find(comment => 
                    comment.subject === 'INSTRUCTIONS'
                );
                
                initialLetterData[letter.id] = {
                    instructions: existingInstructions?.commentText || '',
                    skipAnswer: true
                };
                
                if (existingInstructions?.commentText) {
                    hasExistingInstructions = true;
                }
            });
            
            setLetterData(initialLetterData);
            
            // Set bulk fields based on existing data
            if (hasExistingInstructions) {
                // If all letters have the same instructions, set bulk field
                const allInstructions = Object.values(initialLetterData).map(data => data.instructions);
                const uniqueInstructions = [...new Set(allInstructions)];
                
                if (uniqueInstructions.length === 1 && uniqueInstructions[0]) {
                    setBulkInstructions(uniqueInstructions[0]);
                }
            } else {
                // Reset bulk fields if no existing instructions
                setBulkInstructions('');
            }
            setBulkSkipAnswer(true);
        }
    }, [visible,stackId, selectedLetters.length]);

    const handleBulkInstructionsChange = (value) => {
        console.log('Bulk instructions changed:', value);
        setBulkInstructions(value);
        
        if (selectedLetters.length === 0) {
            console.log('No selected letters found');
            return;
        }

        // Update all individual instruction fields immediately
        const updatedLetterData = { ...letterData };
        selectedLetters.forEach(letter => {
            updatedLetterData[letter.id] = {
                ...updatedLetterData[letter.id],
                instructions: value
            };
        });
        setLetterData(updatedLetterData);
    };

    const handleBulkSkipAnswerChange = (checked) => {
        console.log('Bulk skip answer changed:', checked);
        setBulkSkipAnswer(checked);
        
        if (selectedLetters.length === 0) {
            console.log('No selected letters found for skip answer');
            return;
        }

        // Update all individual skip answer fields immediately
        const updatedLetterData = { ...letterData };
        selectedLetters.forEach(letter => {
            updatedLetterData[letter.id] = {
                ...updatedLetterData[letter.id],
                skipAnswer: checked
            };
        });
        setLetterData(updatedLetterData);
    };

    const handleIndividualInstructionsChange = (letterId, value) => {
        setLetterData(prev => ({
            ...prev,
            [letterId]: {
                ...prev[letterId],
                instructions: value
            }
        }));
    };

    const handleIndividualSkipAnswerChange = (letterId, checked) => {
        setLetterData(prev => ({
            ...prev,
            [letterId]: {
                ...prev[letterId],
                skipAnswer: checked
            }
        }));
    };

    const handleSubmit = () => {
        console.log('Submitting letter data:', letterData);
        
        // Validate that letters with skip answer checked have instructions
        const lettersWithSkipAnswer = Object.keys(letterData).filter(
            letterId => letterData[letterId]?.skipAnswer
        );
        
        const lettersWithoutInstructions = lettersWithSkipAnswer.filter(
            letterId => !letterData[letterId]?.instructions?.trim()
        );
        
        if (lettersWithoutInstructions.length > 0) {
            notification.warning({
                message: 'Instructions required',
                description: 'Letters with "Skip Answer" checked must have instructions.',
            });
            return;
        }

        // Format data for parent component
        const formData = {
            letters: letterData
        };

        onOk(formData);
    };

    const handleCancel = () => {
        // Reset all state
        setBulkInstructions('');
        setBulkSkipAnswer(true);
        setLetterData({});
        setTableData([]);
        onCancel();
    };

    const columns = [
        {
            title: 'Sender',
            dataIndex: 'senderName',
            key: 'senderName',
            width: 200,
            render: (text, record) => (
                <div>
                    <strong>{text}</strong>
                    <br />
                    <small style={{ color: '#666' }}>
                        Letter ID: {record.letterId}
                    </small>
                </div>
            )
        },
        {
            title: 'Instructions',
            dataIndex: 'instructions',
            key: 'instructions',
            render: (text, record) => (
                <Input
                    placeholder="Enter instructions for this letter..."
                    value={letterData[record.letter.id]?.instructions || ''}
                    onChange={(e) => handleIndividualInstructionsChange(record.letter.id, e.target.value)}
                />
            )
        },
        {
            title: 'Skip Answer',
            dataIndex: 'skipAnswer',
            key: 'skipAnswer',
            width: 120,
            render: (text, record) => (
                <div style={{ textAlign: 'center' }}>
                    <Checkbox
                        checked={letterData[record.letter.id]?.skipAnswer || false}
                        onChange={(e) => handleIndividualSkipAnswerChange(record.letter.id, e.target.checked)}
                    />
                </div>
            )
        }
    ];

    return (
        <Modal
            title="Add Instructions to Selected Letters"
            open={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            width={1200}
            okText="Add Instructions"
            cancelText="Cancel"
            destroyOnClose
        >
            {/* Modify All Section */}
            <div style={{ 
                marginBottom: 24, 
                padding: '16px', 
                border: '1px solid #d9d9d9', 
                borderRadius: 8, 
                backgroundColor: '#fafafa' 
            }}>
                <div style={{ marginBottom: 16 }}>
                    <strong style={{ fontSize: '16px', color: '#1890ff' }}>Modify All Selected Letters</strong>
                    <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
                        Set instructions and skip answer for all selected letters at once. <strong>Note:</strong> Instructions are required when "Skip Answer" is checked.
                    </p>
                </div>
                
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                            Instructions for All
                        </label>
                        <Input
                            placeholder="Enter instructions to apply to all letters..."
                            value={bulkInstructions}
                            onChange={(e) => handleBulkInstructionsChange(e.target.value)}
                        />
                    </div>
                    
                    <div style={{ marginTop: '32px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                            Skip Answer for All
                        </label>
                        <Checkbox
                            checked={bulkSkipAnswer}
                            onChange={(e) => handleBulkSkipAnswerChange(e.target.checked)}
                        />
                    </div>
                </div>
            </div>

            <div style={{ marginBottom: 16 }}>
                <strong>Individual Letter Settings:</strong>
                <p style={{ margin: '8px 0 8px 0', color: '#666', fontSize: '12px' }}>
                    <strong>Validation:</strong> Instructions are required when "Skip Answer" is checked.
                </p>
                <div style={{ maxHeight: 500, overflow: 'auto' }}>
                    <Table
                        dataSource={tableData}
                        columns={columns}
                        pagination={false}
                        size="small"
                        bordered
                        style={{ marginTop: 8 }}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default AddInstructionsDialog;
